// React imports
import React, { useState } from 'react';

// General imports
import _ from 'lodash';

// MUI imports
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { useTheme } from '@mui/material/styles';

// FontAwesome imports (fallback if lucide-react not available)
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDownArrowUp } from '@fortawesome/pro-light-svg-icons';

// Try to import lucide-react, fallback to FontAwesome
let ArrowUpDownIcon = null;
try {
    const lucideReact = require('lucide-react');
    ArrowUpDownIcon = lucideReact.ArrowUpDown;
} catch (e) {
    // Fallback to FontAwesome
    ArrowUpDownIcon = ({ size, style }) => <FontAwesomeIcon icon={faArrowDownArrowUp} style={style} />;
}

const SORT_FIELDS = [
    { value: 'price', label: 'Price' },
    { value: 'transitTime', label: 'Transit time' },
    { value: 'departureTime', label: 'Departure time' },
    { value: 'arrivalTime', label: 'Arrival time' },
];

const SORT_DIRECTIONS = [
    { value: 'asc', label: 'Ascending' },
    { value: 'desc', label: 'Descending' },
];

function SortingPanel(props) {
    const { sorting, setSorting } = props;
    const [anchorEl, setAnchorEl] = useState(null);
    const theme = useTheme();

    const isOpen = Boolean(anchorEl);

    // Parse sorting value (format: "field" or "field-direction")
    const parseSorting = (sortValue) => {
        if (!sortValue) return { field: 'price', direction: 'asc' };
        const parts = sortValue.split('-');
        return {
            field: parts[0] || 'price',
            direction: parts[1] || 'asc'
        };
    };

    const currentSort = parseSorting(sorting);
    const [selectedField, setSelectedField] = useState(currentSort.field);
    const [selectedDirection, setSelectedDirection] = useState(currentSort.direction);

    const handleButtonClick = (event) => {
        setAnchorEl(event.currentTarget);
        // Reset to current sorting when opening
        const parsed = parseSorting(sorting);
        setSelectedField(parsed.field);
        setSelectedDirection(parsed.direction);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleFieldChange = (field) => {
        setSelectedField(field);
        // Apply immediately
        setSorting(`${field}-${selectedDirection}`);
    };

    const handleDirectionChange = (direction) => {
        setSelectedDirection(direction);
        // Apply immediately
        setSorting(`${selectedField}-${direction}`);
    };

    const getSortDisplayText = () => {
        return 'Sort';
    };

    return (
        <>
            <Button
                variant="text"
                {...(currentSort.field !== 'price' || currentSort.direction !== 'asc' ? { color: 'primary' } : {})}
                onClick={handleButtonClick}
                sx={{
                    textTransform: 'none',
                    fontSize: '11px !important',
                    px: 1.5,
                    minWidth: '0px',
                    whiteSpace: 'nowrap',
                    borderRadius: 1,
                    height: 30,
                    background: 'none'
                }}
            >
                Sort
                <ArrowUpDownIcon size={14} style={{ marginLeft: '6px', opacity: 0.6 }} />
            </Button>
            <Popover
                open={isOpen}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                slotProps={{ 
                    root: { sx: { zIndex: 1000000 }},
                    paper: {
                        sx: {
                            minWidth: '400px',
                            p: 2,
                            mt: 1,
                        }
                    }
                }}
            >
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 2,
                    }}
                >
                    {/* Sort Field */}
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                            Sort Field
                        </Typography>
                        <FormControl component="fieldset" size="small" fullWidth>
                            <RadioGroup
                                value={selectedField}
                                onChange={(e) => handleFieldChange(e.target.value)}
                            >
                                {SORT_FIELDS.map(field => (
                                    <FormControlLabel
                                        key={field.value}
                                        value={field.value}
                                        control={<Radio size="small" />}
                                        label={<Typography variant="caption">{field.label}</Typography>}
                                    />
                                ))}
                            </RadioGroup>
                        </FormControl>
                    </Box>

                    {/* Sort Direction */}
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                            Sort Direction
                        </Typography>
                        <FormControl component="fieldset" size="small" fullWidth>
                            <RadioGroup
                                value={selectedDirection}
                                onChange={(e) => handleDirectionChange(e.target.value)}
                            >
                                {SORT_DIRECTIONS.map(direction => (
                                    <FormControlLabel
                                        key={direction.value}
                                        value={direction.value}
                                        control={<Radio size="small" />}
                                        label={<Typography variant="caption">{direction.label}</Typography>}
                                    />
                                ))}
                            </RadioGroup>
                        </FormControl>
                    </Box>
                </Box>
            </Popover>
        </>
    );
}

export default SortingPanel;
